import React, { useState, useEffect, useRef } from 'react';
import { Tree, TreeNode } from 'react-organizational-chart';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaBuilding, FaUsers, FaPlus, FaEdit, FaTrash, FaChevronDown, FaChevronRight, FaSearch, FaAngleLeft, FaAngleRight, FaSearchMinus, FaSearchPlus, FaTimes, FaCog, FaUserFriends, FaUser, FaUserTie, FaUsersCog, FaUserGraduate, FaIndustry, FaHospital, FaSchool, FaStore, FaWarehouse, FaLaptop, FaCogs, FaAngry, FaAddressBook, FaAdn } from 'react-icons/fa';
import { toast } from 'react-toastify';
import OrganizationalUnitService from '../services/OrganizationalUnitService';
import OrganizationalUnitTypeService from '../services/OrganizationalUnitTypeService';
import userService from '../services/userService';
import UserPositionService from '../services/UserPositionService';
import SettingsService from '../services/SettingsService';
import UnitTypesModal from './UnitTypesModal';
import ProfileImage from './ProfileImage';
import { API_BASE_URL } from '../configs/apiConfig';
import AppointmentParametersService from '../services/AppointmentParametersService';
import XPortionService from '../services/XPortionService';
import '../assets/styles/OrganizationalChart.css';
import '../layouts/UserDashboard/styles/Tooltip.css';

const OrganizationalChart = () => {
  const [chartData, setChartData] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [collapsedNodes, setCollapsedNodes] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [modalData, setModalData] = useState(null);
  const [draggingUser, setDraggingUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unitTypes, setUnitTypes] = useState([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [organizationName, setOrganizationName] = useState('Your Organization');
  const [organizationLogo, setOrganizationLogo] = useState(null);
  const chartRef = useRef(null);
  const [formKey, setFormKey] = useState(0);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const chartContentRef = useRef(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [draggedUserData, setDraggedUserData] = useState(null);
  const [targetUnitData, setTargetUnitData] = useState(null);
  const [includeAppointment, setIncludeAppointment] = useState(false);
  const [assignmentFormData, setAssignmentFormData] = useState({
    positionTitle: '',
    roleName: '',
    employmentType: 'Full-time',
    positionMultiplier: 1.0,
    roleImportance: 0,
    portion: 1.0,
    impactFactor: 1.0,
    timeValue: 0,
    startDate: new Date().toISOString().split('T')[0],
    endDate: null,
    organizationalLevelName: '',
    organizationalLevelMultiplier: 1.0,
    isManager: false
  });

  // State for form inputs
  const [formInputs, setFormInputs] = useState({
    name: '',
    description: '',
    unitTypeId: unitTypes.length > 0 ? unitTypes[0]?.id : null
  });

  // Update form inputs when modalData changes
  useEffect(() => {
    if (modalData) {
      setFormInputs({
        name: modalData.name || '',
        description: modalData.description || '',
        unitTypeId: modalData.unitTypeId || (unitTypes.length > 0 ? unitTypes[0]?.id : null)
      });
    }
  }, [modalData, unitTypes]);

  // Fetch unit types on component mount
  useEffect(() => {
    const fetchUnitTypes = async () => {
      try {
        const types = await OrganizationalUnitTypeService.getAllTypes();
        setUnitTypes(types || []);
      } catch (err) {
        setUnitTypes([]);
      }
    };
    
    fetchUnitTypes();
  }, []);

  // Fetch organizational chart data and users on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch organization chart
        const chartResponse = await OrganizationalUnitService.getOrganizationChart();
        setChartData(chartResponse);
        
        // Fetch all users without filtering
        const usersResponse = await userService.getAllUsers();
        setUsers(usersResponse);
        
      } catch (err) {
        setError('Failed to load organizational chart data');
        console.error('Error loading chart data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Fetch organization settings
  useEffect(() => {
    const fetchOrganizationSettings = async () => {
      try {
        const response = await SettingsService.getSetting('organization');
        if (response && response.value) {
          const orgSettings = JSON.parse(response.value);
          setOrganizationName(orgSettings.organizationName || 'Your Organization');
          
          // Use OrganizationLogo if available, fall back to organizationLogo
          const logoPath = orgSettings.OrganizationLogo || orgSettings.organizationLogo;
          if (logoPath) {
            // If the path is relative, prepend the API base URL
            const logoUrl = logoPath.startsWith('http') 
              ? logoPath 
              : `${API_BASE_URL}${logoPath}`;
            setOrganizationLogo(logoUrl);
          }
        }
      } catch (error) {
        // Handle error silently
      }
    };
    
    fetchOrganizationSettings();
  }, []);

  // Helper function to get all user IDs assigned to any unit in the chart
  const getAllAssignedUserIds = (nodes) => {
    if (!nodes) return [];
    
    let userIds = [];
    
    // Handle both single node and array of nodes
    const nodeArray = Array.isArray(nodes) ? nodes : [nodes];
    
    // Process each node
    nodeArray.forEach(node => {
      // Add users from current node
      if (node.assignedUsers && node.assignedUsers.length > 0) {
        userIds = userIds.concat(node.assignedUsers.map(user => user.userId));
      }
      
      // Add users from children nodes recursively
      if (node.children && node.children.length > 0) {
        userIds = userIds.concat(getAllAssignedUserIds(node.children));
      }
    });
    
    return userIds;
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user => 
    (user.firstName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) || 
    (user.lastName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) || 
    (user.email?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  // Handle node collapse/expand
  const handleToggleCollapse = (nodeId) => {
    if (collapsedNodes.includes(nodeId)) {
      setCollapsedNodes(collapsedNodes.filter(id => id !== nodeId));
    } else {
      setCollapsedNodes([...collapsedNodes, nodeId]);
    }
  };

  // Check if a node is collapsed
  const isNodeCollapsed = (nodeId) => {
    return collapsedNodes.includes(nodeId);
  };

  // Handle node selection
  const handleSelectNode = (node) => {
    setSelectedNode(node.id === selectedNode ? null : node.id);
  };

  // Open modal for adding, editing, or deleting a unit
  const openModal = (type, node = null) => {
    setModalType(type);
    
    // For add operation, only keep the parentId from the node
    if (type === 'add') {
      // When adding, we only need the parentId from the node
      setModalData({
        parentId: node?.id || null,  // This is the node we clicked on, its ID becomes the parent ID
        // Reset other fields
        name: '',
        description: '',
        unitTypeId: unitTypes.length > 0 ? unitTypes[0].id : null
      });
      
      // Reset form inputs directly
      setFormInputs({
        name: '',
        description: '',
        unitTypeId: unitTypes.length > 0 ? unitTypes[0]?.id : null
      });
    } else {
      // For edit or delete, use the full node data
      setModalData(node);
      
      // Set form inputs from node data
      if (node) {
        setFormInputs({
          name: node.name || '',
          description: node.description || '',
          unitTypeId: node.unitTypeId || (unitTypes.length > 0 ? unitTypes[0]?.id : null)
        });
      }
    }
    
    // Increment form key to force re-render of the form with fresh inputs
    setFormKey(prevKey => prevKey + 1);
    
    setShowModal(true);
    
    // Force the modal to be on top of everything
    document.body.style.overflow = 'hidden';
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setModalType('');
    setModalData(null);
    
    // Restore scrolling
    document.body.style.overflow = 'auto';
  };

  // Handle form submission in modal
  const handleFormSubmit = async (formData) => {
    try {
      if (modalType === 'add') {
        // Add new unit logic
        await addUnit(formData);
      } else if (modalType === 'edit') {
        // Edit unit logic
        await editUnit(formData);
      } else if (modalType === 'delete') {
        // Delete unit logic
        await deleteUnit(modalData.id);
      }
      closeModal();
    } catch (err) {
      setError('Failed to process your request');
    }
  };

  // Add a new unit
  const addUnit = async (unitData) => {
    try {
      // Find the selected unit type to get its properties
      const selectedUnitType = unitTypes.find(type => type.id === parseInt(unitData.unitTypeId));
      
      if (!selectedUnitType) {
        throw new Error('Selected unit type not found. Please ensure unit types are loaded.');
      }
      
      const newUnitData = {
        name: unitData.name,
        description: unitData.description,
        unitTypeId: parseInt(unitData.unitTypeId),
        parentUnitId: unitData.parentId, // Use the parentId directly
        // Add the required unit type fields with exact field names as expected by the API
        UnitTypeIcon: selectedUnitType.icon,
        UnitTypeName: selectedUnitType.name,
        UnitTypeColor: selectedUnitType.color
      };
      
      const response = await OrganizationalUnitService.addUnit(newUnitData);
      
      // Refresh the chart data
      const updatedChart = await OrganizationalUnitService.getOrganizationChart();
      setChartData(updatedChart);
      
      return response;
    } catch (err) {
      throw err;
    }
  };

  // Edit an existing unit
  const editUnit = async (unitData) => {
    try {
      // Find the selected unit type to get its properties
      const selectedUnitType = unitTypes.find(type => type.id === parseInt(unitData.unitTypeId));
      
      if (!selectedUnitType) {
        throw new Error('Selected unit type not found. Please ensure unit types are loaded.');
      }
      
      // Find the current unit to get its parent ID
      const currentUnit = await OrganizationalUnitService.getUnitById(unitData.id);
      
      const updatedUnitData = {
        id: unitData.id,
        name: unitData.name,
        description: unitData.description,
        unitTypeId: parseInt(unitData.unitTypeId),
        parentUnitId: currentUnit.parentUnitId, // Keep the existing parent ID
        // Add the required unit type fields with exact field names as expected by the API
        UnitTypeIcon: selectedUnitType.icon,
        UnitTypeName: selectedUnitType.name,
        UnitTypeColor: selectedUnitType.color
      };
      
      await OrganizationalUnitService.updateUnit(unitData.id, updatedUnitData);
      
      // Refresh the chart data
      const updatedChart = await OrganizationalUnitService.getOrganizationChart();
      setChartData(updatedChart);
    } catch (err) {
      throw err;
    }
  };

  // Delete a unit
  const deleteUnit = async (unitId) => {
    try {
      await OrganizationalUnitService.deleteUnit(unitId);
      
      // Refresh the chart data
      const updatedChart = await OrganizationalUnitService.getOrganizationChart();
      setChartData(updatedChart);
      
      // Refresh the users list to include any users that were in the deleted unit
      const usersResponse = await userService.getAllUsers();
      const assignedUserIds = updatedChart ? getAllAssignedUserIds(updatedChart) : [];
      const unassignedUsers = usersResponse.filter(user => !assignedUserIds.includes(user.id));
      setUsers(unassignedUsers);
    } catch (err) {
      throw err;
    }
  };

  // Handle drag start
  const onDragStart = (result) => {
    const { draggableId } = result;
    const user = users.find(u => u.id.toString() === draggableId);
    setDraggingUser(user);
  };

  // Add this new useEffect for fetching appointment parameters
  const [employmentTypes, setEmploymentTypes] = useState([]);
  const [experienceLevels, setExperienceLevels] = useState([]);
  const [educationLevels, setEducationLevels] = useState([]);
  const [organizationalLevels, setOrganizationalLevels] = useState([]);
  const [latestXPortion, setLatestXPortion] = useState(null);
  const [unitTypeMultiplier, setUnitTypeMultiplier] = useState(1.0);

  useEffect(() => {
    const fetchAppointmentParameters = async () => {
      try {
        const [empTypes, expLevels, eduLevels, orgLevels, xPortion] = await Promise.all([
          AppointmentParametersService.getAllEmploymentTypes(),
          AppointmentParametersService.getAllExperienceLevels(),
          AppointmentParametersService.getAllEducationLevels(),
          AppointmentParametersService.getAllOrganizationalLevels(),
          XPortionService.getLatest()
        ]);

        setEmploymentTypes(empTypes || []);
        setExperienceLevels(expLevels || []);
        setEducationLevels(eduLevels || []);
        setOrganizationalLevels(orgLevels || []);
        setLatestXPortion(xPortion);

        // Update the assignment form data with the latest XPortion value
        setAssignmentFormData(prev => ({
          ...prev,
          xPortion: xPortion?.value ?? 1.0
        }));
      } catch (err) {
        toast.error('Failed to load appointment parameters');
      }
    };

    fetchAppointmentParameters();
  }, []);

  // Add effect to fetch unit type multiplier when target unit changes
  useEffect(() => {
    const fetchUnitTypeMultiplier = async () => {
      if (targetUnitData?.unit?.unitTypeId) {
        try {
          const unitTypeData = await OrganizationalUnitTypeService.getTypeById(targetUnitData.unit.unitTypeId);
          if (unitTypeData && unitTypeData.multiplier) {
            setUnitTypeMultiplier(unitTypeData.multiplier);
            
            // Update the assignment form data with the unit type multiplier
            setAssignmentFormData(prev => ({
              ...prev,
              positionMultiplier: unitTypeData.multiplier
            }));
          }
        } catch (err) {
          // Handle error silently
        }
      }
    };

    if (targetUnitData) {
      fetchUnitTypeMultiplier();
    }
  }, [targetUnitData]);

  // Handle drag end
  const onDragEnd = async (result) => {
    setDraggingUser(null);
    
    const { destination, source, draggableId } = result;
    
    // Return early if there's no destination or if dropping in non-unit area
    if (!destination || !destination.droppableId.startsWith('unit-')) {
      return;
    }
    
    const unitId = parseInt(destination.droppableId.split('-')[1]);
    const userId = parseInt(draggableId);
    
    // Find the target unit data
    const findUnit = (node) => {
      if (node.id === unitId) return node;
      if (node.children) {
        for (const child of node.children) {
          const found = findUnit(child);
          if (found) return found;
        }
      }
      return null;
    };

    const targetUnit = Array.isArray(chartData) 
      ? chartData.map(findUnit).find(u => u)
      : findUnit(chartData);

    // Return early if target unit is not found
    if (!targetUnit) {
      toast.error('Invalid drop location. Please drop users onto a valid organizational unit.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      return;
    }

    // Check if user is already assigned to this unit
    if (targetUnit.assignedUsers && 
        targetUnit.assignedUsers.some(user => user.userId === userId)) {
      toast.error('This user is already assigned to this unit', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      return;
    }

    // Store the dragged user and target unit data
    setDraggedUserData({
      userId,
      user: users.find(u => u.id === userId)
    });
    setTargetUnitData({
      unitId,
      unit: targetUnit
    });
    
    // Set default position title based on unit name
    setAssignmentFormData(prev => ({
      ...prev,
      positionTitle: `${targetUnit.name}`
    }));
    
    // Show the assignment modal
    setShowAssignModal(true);
  };

  // Handle assignment form submission
  const handleAssignmentSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (includeAppointment) {
        // Convert dates to UTC format with time component
        const startDate = assignmentFormData.startDate ? 
          new Date(assignmentFormData.startDate + 'T00:00:00.000Z').toISOString() : null;
        const endDate = assignmentFormData.endDate ? 
          new Date(assignmentFormData.endDate + 'T00:00:00.000Z').toISOString() : null;

        // Get the latest X-Portion value
        let xPortionValue = 1.0;
        try {
          const latestXPortion = await XPortionService.getLatest();
          xPortionValue = latestXPortion.value;
        } catch (xPortionError) {
          // Use default value if error occurs
        }

        // Get the selected values from the dropdowns
        const selectedEmploymentType = employmentTypes.find(
          type => `${type.name}|${type.multiplier}` === `${assignmentFormData.employmentType}|${assignmentFormData.employmentTypeMultiplier}`
        );
        
        const selectedExperienceLevel = experienceLevels.find(
          level => `${level.name}|${level.multiplier}` === `${assignmentFormData.experienceLevelName}|${assignmentFormData.experienceLevelMultiplier}`
        );
        
                  const selectedEducationLevel = educationLevels.find(
            level => `${level.name}|${level.multiplier}` === `${assignmentFormData.educationLevelName}|${assignmentFormData.educationlevelMultiplier}`
          );
        
        const selectedOrganizationalLevel = organizationalLevels.find(
          level => `${level.name}|${level.multiplier}` === `${assignmentFormData.organizationalLevelName}|${assignmentFormData.organizationalLevelMultiplier}`
        );

        // Use assign endpoint with both position and appointment data
        await UserPositionService.assignUserToUnit({
          position: {
            userId: draggedUserData.userId,
            organizationalUnitId: targetUnitData.unitId,
            positionTitle: assignmentFormData.positionTitle,
            assignmentDate: startDate,
            isManager: assignmentFormData.isManager
          },
          appointment: {
            userId: draggedUserData.userId,
            userFullName: `${draggedUserData.user.firstName || ''} ${draggedUserData.user.lastName || ''}`.trim(),
            positionTitle: assignmentFormData.positionTitle,
            organizationalUnitName: targetUnitData.unit.name,
            roleName: assignmentFormData.roleName || '',
            
            // Employment type
            employmentType: selectedEmploymentType?.name || 'Full-time',
            employmentTypeMultiplier: selectedEmploymentType?.multiplier || 1.0,
            
            // Position multiplier
            positionMultiplier: parseFloat(assignmentFormData.positionMultiplier) || 1.0,
            
            // Experience level
            experienceLevelName: selectedExperienceLevel?.name || null,
            experienceLevelMultiplier: selectedExperienceLevel?.multiplier || 1.0,
            
            // Education level
            educationLevelName: selectedEducationLevel?.name || null,
            educationlevelMultiplier: selectedEducationLevel?.multiplier || 1.0,
            
            // Organizational level
            organizationalLevelName: selectedOrganizationalLevel?.name || null,
            organizationalLevelMultiplier: selectedOrganizationalLevel?.multiplier || 1.0,
            
            // Other factors
            roleImportance: calculateRoleImportance({
              positionMultiplier: parseFloat(assignmentFormData.positionMultiplier) || 1.0,
              experienceLevelMultiplier: selectedExperienceLevel?.multiplier || 1.0,
              educationlevelMultiplier: selectedEducationLevel?.multiplier || 1.0,
              organizationalLevelMultiplier: selectedOrganizationalLevel?.multiplier || 1.0,
              impactFactor: parseFloat(assignmentFormData.impactFactor) || 1.0
            }),
            impactFactor: parseFloat(assignmentFormData.impactFactor) || 0,
            
            // X-Portion value from the latest entry
            xPortion: xPortionValue,
            
            // Dates
            startDate: startDate,
            endDate: endDate
          }
        });
      } else {
        // Use simple add endpoint with just position data
        const currentUtcDate = new Date().toISOString();

        // Use simple add endpoint with just position data
        await UserPositionService.addUserPosition({
          userId: draggedUserData.userId,
          organizationalUnitId: targetUnitData.unitId,
          positionTitle: assignmentFormData.positionTitle,
          assignmentDate: currentUtcDate,
          isManager: assignmentFormData.isManager
        });
      }

      // Refresh the chart data
      const updatedChart = await OrganizationalUnitService.getOrganizationChart();
      setChartData(updatedChart);
      
      // Show success notification
      toast.success(`User ${(draggedUserData.user.firstName || '')} ${(draggedUserData.user.lastName || '')} has been successfully assigned to ${targetUnitData.unit.name}`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      
      // Close the modal and reset form
      setShowAssignModal(false);
      setDraggedUserData(null);
      setTargetUnitData(null);
      setIncludeAppointment(false);
      setAssignmentFormData({
        positionTitle: '',
        roleName: '',
        employmentType: 'Full-time',
        positionMultiplier: 1.0,
        roleImportance: 0,
        portion: 1.0,
        impactFactor: 1.0,
        timeValue: 0,
        startDate: new Date().toISOString().split('T')[0],
        endDate: null,
        organizationalLevelName: '',
        organizationalLevelMultiplier: 1.0,
        isManager: false
      });
    } catch (err) {
      toast.error('Failed to assign user to unit');
    }
  };

  // Handle drag update
  const onDragUpdate = (result) => {
    // Optional: Add visual feedback during dragging
  };

  // Utility function to get user initials consistently
  const getUserInitials = (user) => {
    // For users with only username (like HR2)
    if ((!user.firstName && !user.lastName && user.username) || 
        (user.userId && !user.userFullName?.trim() && user.username)) {
      return user.username.slice(0, 2);
    }
    
    // For users with userFullName (from chart)
    if (user.userFullName && user.userFullName.trim()) {
      const nameParts = user.userFullName.trim().split(' ');
      if (nameParts.length >= 2) {
        return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`;
      }
      return user.userFullName.slice(0, 2);
    }
    
    // For users with firstName/lastName (from user list)
    if (user.firstName || user.lastName) {
      if (user.firstName && user.lastName) {
        return `${user.firstName[0]}${user.lastName[0]}`;
      }
      const singleName = user.firstName || user.lastName;
      return singleName.slice(0, 2);
    }
    
    // Try to find the original user data by userId
    const originalUser = users.find(u => u.id === user.userId);
    if (originalUser?.username) {
      return originalUser.username.slice(0, 2);
    }
    
    // Last resort
    return 'Un';
  };

  // Function to get consistent background color for a user
  const getUserAvatarColor = (user) => {
    // Get user ID consistently
    const userId = user.userId || user.id;
    
    if (!userId) {
        // Fallback for users without ID (shouldn't happen in normal cases)
        return '#6c757d';
    }

    // Use predefined hue values to ensure distinct colors
    const hues = [
        0,    // red
        210,  // blue
        120,  // green
        270,  // purple
        30,   // orange
        180,  // cyan
        330,  // pink
        60,   // yellow
        150,  // blue-green
        300,  // magenta
        90,   // yellow-green
        240,  // indigo
        15,   // red-orange
        195,  // blue-cyan
        45,   // orange-yellow
        225,  // blue-purple
        165,  // green-cyan
        285,  // purple-magenta
        75,   // yellow-green
        255   // blue-indigo
    ];

    // Use userId to select a hue from the array
    const hueIndex = userId % hues.length;
    const hue = hues[hueIndex];

    // Generate saturation and lightness based on userId
    const saturation = 65 + (userId % 15); // 65-80%
    const lightness = 45 + (userId % 10);  // 45-55%

    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  // Add this function to normalize user data
  const normalizeUserData = (user) => {
    // If the user has userFullName but no firstName/lastName (chart data)
    if (user.userFullName && !user.firstName && !user.lastName) {
      const nameParts = user.userFullName.split(' ');
      if (nameParts.length >= 2) {
        user.firstName = nameParts[0];
        user.lastName = nameParts[1];
      }
    }
    return user;
  };

  // Update renderUserAvatar to use normalized data
  const renderUserAvatar = (user) => {
    const normalizedUser = normalizeUserData(user);
    const initials = getUserInitials(normalizedUser);
    
    // Check for both imageUrl and userImageURL
    const userImage = user.imageUrl || user.userImageURL;
    
    // Check if user is a manager to apply manager styling
    const isManager = user.isManager === true;
    
    const avatarStyle = !userImage ? {
      backgroundColor: getUserAvatarColor(normalizedUser),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: '14px',
      fontWeight: '500'
    } : {};
    
    return (
      <div className={`user-avatar ${isManager ? 'manager-avatar' : ''}`} style={avatarStyle}>
        {userImage ? (
          <ProfileImage
            imageUrl={userImage}
            alt={normalizedUser.firstName && normalizedUser.lastName ? 
              `${normalizedUser.firstName} ${normalizedUser.lastName}` : 
              normalizedUser.username}
          />
        ) : (
          <span>{initials}</span>
        )}
      </div>
    );
  };

  // Handle opening manager modal
  const handleOpenManagerModal = (user, unitId, unitName) => {
    setSelectedUserForManager(user);
    setSelectedUnitForManager({ id: unitId, name: unitName });
    setTempManagerStatus(!!user.isManager); // Convert to boolean
    setShowUserManagerModal(true);
  };

  // Handle saving manager status from modal
  const handleSaveManagerStatus = async () => {
    try {
      if (!selectedUserForManager || !selectedUnitForManager) return;
      
      // Find the user position for this user in this unit
      const positions = await UserPositionService.getUserPositionsByUnitId(selectedUnitForManager.id);
      const userPosition = positions.find(p => p.userId === selectedUserForManager.userId);
      
      if (userPosition) {
        // Update the manager status with complete position data
        const updatedPositionData = {
          id: userPosition.id,
          userId: userPosition.userId,
          userFullName: userPosition.userFullName,
          userImageURL: userPosition.userImageURL,
          organizationalUnitId: userPosition.organizationalUnitId,
          organizationalUnitName: userPosition.organizationalUnitName,
          positionTitle: userPosition.positionTitle,
          assignmentDate: userPosition.assignmentDate,
          isManager: tempManagerStatus  // This is the key field we're updating
                  };
          
          await UserPositionService.updateUserPosition(userPosition.id, updatedPositionData);
        
        // Refresh the chart data
        const updatedChart = await OrganizationalUnitService.getOrganizationChart();
        setChartData(updatedChart);
        
        // Show success notification
        const userName = selectedUserForManager.userFullName || 
          `${selectedUserForManager.firstName || ''} ${selectedUserForManager.lastName || ''}`.trim() ||
          selectedUserForManager.username || 'User';
        const status = tempManagerStatus ? 'promoted to manager' : 'removed from manager role';
        toast.success(`${userName} has been ${status} in ${selectedUnitForManager.name}`, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        
        // Close modal
        setShowUserManagerModal(false);
        setSelectedUserForManager(null);
        setSelectedUnitForManager(null);
        setTempManagerStatus(false);
      } else {
        console.error('User position not found for user:', selectedUserForManager.userId, 'in unit:', selectedUnitForManager.id);
        toast.error('User position not found. Please refresh and try again.');
      }
    } catch (err) {
      console.error('Error updating manager status:', err);
      console.error('Error details:', err.response?.data || err.message);
      toast.error('Failed to update manager status: ' + (err.response?.data?.message || err.message));
    }
  };

  // Render the organizational chart tree
  const renderTree = (node) => {
    if (!node) return null;
    
    const hasChildren = node.children && node.children.length > 0;
    const isCollapsed = isNodeCollapsed(node.id);
    const unitType = unitTypes.find(type => type.id === node.unitTypeId);
    const unitColor = unitType?.color || '#cccccc';
    
    // Map unit type icons to React Icons components
    const getIconComponent = (iconName) => {
      if (!iconName) return FaBuilding;

      // Convert icon name to lowercase for case-insensitive matching
      const normalizedIconName = iconName.toLowerCase();

      const iconMap = {
        // Original mappings
        'building': FaBuilding,
        'users': FaUsers,
        'user': FaUser,
        'user-friends': FaUserFriends,
        'user-tie': FaUserTie,
        'users-cog': FaUsersCog,
        'user-graduate': FaUserGraduate,
        'industry': FaIndustry,
        'hospital': FaHospital,
        'school': FaSchool,
        'store': FaStore,
        'warehouse': FaWarehouse,
        'laptop': FaLaptop,
        'cogs': FaCogs,
        // Add mappings for the specific icons from your API
        'angry': FaAngry,
        'addressbook': FaAddressBook,
        'adn': FaAdn,
        // Add variations of names
        'fa-building': FaBuilding,
        'fa-users': FaUsers,
        'fa-user': FaUser
      };

      // Try exact match first
      const IconComponent = iconMap[normalizedIconName];
      if (IconComponent) return IconComponent;

      // Try without 'fa-' prefix
      const withoutPrefix = normalizedIconName.replace('fa-', '');
      if (iconMap[withoutPrefix]) return iconMap[withoutPrefix];

      // Try without spaces and hyphens
      const withoutHyphens = withoutPrefix.replace(/[-\s]/g, '');
      if (iconMap[withoutHyphens]) return iconMap[withoutHyphens];

      // Default to building icon if no match found
      return FaBuilding;
    };

    const IconComponent = getIconComponent(unitType?.icon);
    
    const handleDeleteUser = async (userId, userName) => {
      if (window.confirm(`Are you sure you want to remove ${userName} from ${node.name}?`)) {
        try {
          // Find the user position for this user in this unit
          const positions = await UserPositionService.getUserPositionsByUnitId(node.id);
          const userPosition = positions.find(p => p.userId === userId);
          
          if (userPosition) {
            await UserPositionService.deleteUserPosition(userPosition.id);
            
            // Refresh the chart data
            const updatedChart = await OrganizationalUnitService.getOrganizationChart();
            setChartData(updatedChart);
            
            toast.success(`${userName} has been removed from ${node.name}`);
          }
        } catch (err) {
          toast.error('Failed to remove user from unit');
        }
      }
    };
    
    const nodeContent = (
      <div className="org-chart-node">
        <Droppable droppableId={`unit-${node.id}`}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`org-unit ${selectedNode === node.id ? 'selected' : ''} ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
              style={{ borderColor: unitColor }}
              onClick={() => handleSelectNode(node)}
            >
              <div className="org-unit-header" style={{ backgroundColor: unitColor }}>
                <IconComponent className="org-unit-icon" />
                <div className="org-unit-title">
                  {node.name}
                  <div style={{ fontSize: '12px', opacity: 0.8 }}>
                    {unitType?.name || 'Unknown Type'}
                  </div>
                </div>
                <div className="org-unit-actions">
                  <button onClick={(e) => { e.stopPropagation(); openModal('edit', node); }} title="Edit Unit">
                    <FaEdit />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); openModal('delete', node); }} title="Delete Unit">
                    <FaTrash />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); openModal('add', node); }} title="Add Child Unit">
                    <FaPlus />
                  </button>
                  {hasChildren && (
                    <button onClick={(e) => { e.stopPropagation(); handleToggleCollapse(node.id); }} title={isCollapsed ? "Expand" : "Collapse"}>
                      {isCollapsed ? <FaChevronRight /> : <FaChevronDown />}
                    </button>
                  )}
                </div>
              </div>
              <div className="org-unit-content">
                {node.description && (
                  <div className="org-unit-description">
                    {node.description}
                  </div>
                )}
                <div className="org-unit-users">
                  {node.assignedUsers && node.assignedUsers.slice(0, 5).map((user, index) => (
                    <div key={index} className="org-unit-user-avatar">
                      <OverlayTrigger
                        placement="bottom"
                        delay={{ show: 200, hide: 100 }}
                        overlay={
                          <Tooltip id={`tooltip-${user.userId}-${node.id}`} className="bs-tooltip-bottom">
                            {user.userFullName || 'No name available'}
                          </Tooltip>
                        }
                      >
                        <div 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenManagerModal(user, node.id, node.name);
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          {renderUserAvatar(user)}
                        </div>
                      </OverlayTrigger>
                      <button 
                        className="delete-user-badge"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteUser(user.userId, user.userFullName);
                        }}
                        title={`Remove ${user.userFullName} from ${node.name}`}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                  {node.assignedUsers && node.assignedUsers.length > 5 && (
                    <button
                      className="show-more-users"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedUnitUsers({ users: node.assignedUsers, unitName: node.name, unitId: node.id });
                        setShowUsersModal(true);
                      }}
                      title={`Show all ${node.assignedUsers.length} users`}
                    >
                      +{node.assignedUsers.length - 5}
                    </button>
                  )}
                  {provided.placeholder}
                </div>
              </div>
            </div>
          )}
        </Droppable>
      </div>
    );

    return (
      <TreeNode key={node.id} label={nodeContent}>
        {!isCollapsed && hasChildren && node.children.map(child => renderTree(child))}
      </TreeNode>
    );
  };

  // Add zoom and pan handlers
  const handleWheel = (e) => {
    e.preventDefault();
    
    // Get the mouse position relative to the chart content
    const rect = chartContentRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Calculate zoom factor based on wheel direction
    const zoomFactor = e.deltaY < 0 ? 1.1 : 0.9;
    const newScale = Math.min(Math.max(0.1, scale * zoomFactor), 2);

    // Calculate the point under the mouse in the unscaled space
    const pointX = (mouseX - position.x) / scale;
    const pointY = (mouseY - position.y) / scale;

    // Calculate new position to zoom into mouse position
    const newPosition = {
      x: mouseX - pointX * newScale,
      y: mouseY - pointY * newScale
    };

    setScale(newScale);
    setPosition(newPosition);
  };

  useEffect(() => {
    const content = chartContentRef.current;
    if (content) {
      const wheelHandler = (e) => handleWheel(e);
      content.addEventListener('wheel', wheelHandler, { passive: false });
      return () => content.removeEventListener('wheel', wheelHandler);
    }
  }, [scale, position]);

  const handleMouseDown = (e) => {
    if (e.button === 0) { // Left click only
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
      e.preventDefault();
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleZoomIn = () => {
    setScale(Math.min(scale * 1.2, 2));
  };

  const handleZoomOut = () => {
    setScale(Math.max(scale * 0.8, 0.1));
  };

  const handleZoomReset = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const [showUsersModal, setShowUsersModal] = useState(false);
  const [selectedUnitUsers, setSelectedUnitUsers] = useState(null);

  // Add this new component inside the OrganizationalChart component
  const UsersModal = ({ users, unitName, unitId, onClose }) => (
    <div className="org-chart-modal-overlay">
      <div className="org-chart-modal">
        <div className="org-chart-modal-header">
          <h3>Users in {unitName}</h3>
          <button className="org-chart-modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="org-chart-modal-body">
          <div className="unit-users-list">
            {users.map((user, index) => (
              <div key={index} className="unit-user-item">
                <OverlayTrigger
                  placement="bottom"
                  delay={{ show: 200, hide: 100 }}
                  overlay={
                    <Tooltip id={`modal-tooltip-${user.userId}`} className="bs-tooltip-bottom">
                      {user.userFullName || 'No name available'}
                    </Tooltip>
                  }
                >
                  <div 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenManagerModal(user, unitId, unitName);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {renderUserAvatar(user)}
                  </div>
                </OverlayTrigger>
                <span className="user-name">{user.userFullName}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Add new state for unit types modal
  const [showUnitTypesModal, setShowUnitTypesModal] = useState(false);
  const [showUserManagerModal, setShowUserManagerModal] = useState(false);
  const [selectedUserForManager, setSelectedUserForManager] = useState(null);
  const [selectedUnitForManager, setSelectedUnitForManager] = useState(null);
  const [tempManagerStatus, setTempManagerStatus] = useState(false);

  // Add handlers for unit types
  const handleSaveUnitType = async (unitTypeData) => {
    try {
      if (unitTypeData.id) {
        await OrganizationalUnitTypeService.updateType(unitTypeData.id, unitTypeData);
      } else {
        await OrganizationalUnitTypeService.addType(unitTypeData);
      }
      
      // Refresh unit types
      const types = await OrganizationalUnitTypeService.getAllTypes();
      setUnitTypes(types || []);
    } catch (err) {
      throw err;
    }
  };

  const handleDeleteUnitType = async (typeId) => {
    try {
      await OrganizationalUnitTypeService.deleteType(typeId);
      
      // Refresh unit types
      const types = await OrganizationalUnitTypeService.getAllTypes();
      setUnitTypes(types || []);
    } catch (err) {
      throw err;
    }
  };

  // Add this function after the useEffect for fetchAppointmentParameters
  const calculateRoleImportance = (formData) => {
    // Convert all values to numbers and ensure they exist
    const positionMult = Number(formData.positionMultiplier || 1.0);
    const experienceMult = Number(formData.experienceLevelMultiplier || 1.0);
            const educationMult = Number(formData.educationlevelMultiplier || 1.0);
    const organizationalMult = Number(formData.organizationalLevelMultiplier || 1.0);
    const impactMult = Number(formData.impactFactor || 1.0);
    
    // Calculate the exact value
    const exactValue = positionMult * experienceMult * educationMult * organizationalMult * impactMult;
    
    // Return the value without any rounding or truncation
    return exactValue;
  };

  // Add useEffect to update role importance when multipliers change
  useEffect(() => {
    if (includeAppointment) {
      const importance = calculateRoleImportance(assignmentFormData);
      setAssignmentFormData(prev => ({
        ...prev,
        roleImportance: importance
      }));
    }
  }, [
    assignmentFormData.positionMultiplier,
    assignmentFormData.experienceLevelMultiplier,
                assignmentFormData.educationlevelMultiplier,
    assignmentFormData.organizationalLevelMultiplier,
    assignmentFormData.impactFactor,
    includeAppointment
  ]);

  // Show loading state first
  if (loading) {
    return (
      <div className="org-chart-loading">
        <div className="loading-spinner"></div>
        <p>Loading organizational chart...</p>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="org-chart-error">
        <h3>Error</h3>
        <p>{error}</p>
        <button 
          className="org-chart-btn org-chart-btn-primary"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }

  // Show empty state if no chart data and not in modal
  if (!chartData && !showModal) {
    return (
      <div className="org-chart-empty">
        <h3>No Organizational Structure Found</h3>
        <p>Start by creating your organization's root unit.</p>
        <button 
          className="org-chart-btn org-chart-btn-primary"
          onClick={() => {
            openModal('add', { id: null, parentId: null });
          }}
        >
          <FaPlus /> Create Root Unit
        </button>
      </div>
    );
  }

  // Special case: Modal is open but no chart data
  if (!chartData && showModal) {
    return (
      <div className="org-chart-container">
        <div className="org-chart-modal-overlay">
          <div className="org-chart-modal-content">
            <div className="org-chart-modal-header">
              <h3 className="org-chart-modal-title">
                {modalType === 'add' ? 'Add Unit' : modalType === 'edit' ? 'Edit Unit' : 'Delete Unit'}
              </h3>
              <button className="org-chart-modal-close" onClick={closeModal}>&times;</button>
            </div>
            <div className="org-chart-modal-body">
              {modalType === 'delete' ? (
                <>
                  <p>Are you sure you want to delete "{modalData?.name}"? This action cannot be undone.</p>
                  {modalData?.children && modalData.children.length > 0 && (
                    <div className="org-chart-alert org-chart-alert-warning">
                      <strong>Warning:</strong> This unit has child units. Deleting it will also delete all child units.
                    </div>
                  )}
                  <div className="org-chart-modal-footer">
                    <button className="org-chart-btn org-chart-btn-secondary" onClick={closeModal}>Cancel</button>
                    <button 
                      className="org-chart-btn org-chart-btn-danger" 
                      onClick={() => {
                        deleteUnit(modalData.id);
                        closeModal();
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </>
              ) : (
                <form 
                  key={formKey}
                  onSubmit={(e) => {
                    e.preventDefault();
                    const formData = {
                      id: modalData?.id,
                      name: formInputs.name,
                      description: formInputs.description,
                      unitTypeId: formInputs.unitTypeId,
                      parentId: modalData?.parentId
                    };
                    handleFormSubmit(formData);
                  }}
                >
                  <div className="form-group">
                    <label className="form-label" htmlFor="name">Unit Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="form-control"
                      value={formInputs.name}
                      onChange={(e) => setFormInputs({ ...formInputs, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="description">Description</label>
                    <textarea
                      id="description"
                      name="description"
                      className="form-control"
                      value={formInputs.description}
                      onChange={(e) => setFormInputs({ ...formInputs, description: e.target.value })}
                      rows="3"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="unitTypeId">Unit Type</label>
                    {unitTypes.length > 0 ? (
                      <select
                        id="unitTypeId"
                        name="unitTypeId"
                        className="form-control"
                        value={formInputs.unitTypeId || ''}
                        onChange={(e) => setFormInputs({ ...formInputs, unitTypeId: parseInt(e.target.value) })}
                        required
                      >
                        <option value="">Select a unit type</option>
                        {unitTypes.map(type => (
                          <option key={type.id} value={type.id}>{type.name}</option>
                        ))}
                      </select>
                    ) : (
                      <div className="org-chart-alert org-chart-alert-warning">
                        No unit types available. Please create unit types first.
                      </div>
                    )}
                  </div>
                  <div className="org-chart-modal-footer">
                    <button type="button" className="org-chart-btn org-chart-btn-secondary" onClick={closeModal}>Cancel</button>
                    <button 
                      type="submit" 
                      className="org-chart-btn org-chart-btn-primary"
                      disabled={unitTypes.length === 0}
                    >
                      {modalType === 'add' ? 'Add' : 'Save'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // For the final return statement at the end of the component
  return (
    <div className="org-chart-container">
      {error && (
        <div className="org-chart-error-message" style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: '#ff4444',
          color: 'white',
          padding: '10px 20px',
          borderRadius: '4px',
          zIndex: 1000,
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
        }}>
          {error}
        </div>
      )}
      <div className="org-chart-header">
        <div style={{ flex: 1 }}></div>
        <div className="org-chart-actions">
          <button 
            className="org-chart-btn org-chart-btn-secondary me-2"
            onClick={() => setShowUnitTypesModal(true)}
            title="Manage Unit Types"
          >
            <FaCog /> Unit Types
          </button>
          <button 
            className="org-chart-btn org-chart-btn-primary"
            title={selectedNode ? "Add child to selected unit" : "Add root unit"}
            onClick={() => {
              if (selectedNode) {
                const findNode = (node, id) => {
                  if (node.id === id) return node;
                  if (node.children) {
                    for (const child of node.children) {
                      const found = findNode(child, id);
                      if (found) return found;
                    }
                  }
                  return null;
                };
                
                const nodeToAddTo = findNode(chartData, selectedNode);
                if (nodeToAddTo) {
                  openModal('add', nodeToAddTo);
                } else {
                  openModal('add', chartData);
                }
              } else {
                openModal('add', chartData);
              }
            }}
          >
            <FaPlus /> {selectedNode ? "Add Child Unit" : "Add Unit"}
          </button>
        </div>
      </div>
      <DragDropContext
        onDragStart={onDragStart}
        onDragUpdate={onDragUpdate}
        onDragEnd={onDragEnd}
      >
        <div className="org-chart-content">
          <div className={`users-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
            <div className="sidebar-header">
              {!sidebarCollapsed && (
                <div className="search-input-wrapper" style={{ flex: 1 }}>
                  <FaSearch className="search-icon" />
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              )}
              <button 
                className="sidebar-toggle-btn"
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                {sidebarCollapsed ? <FaAngleRight /> : <FaAngleLeft />}
              </button>
            </div>
            {!sidebarCollapsed && (
              <Droppable droppableId="users-list">
                {(provided) => (
                  <div
                    className="users-list"
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {filteredUsers.length > 0 ? (
                      filteredUsers.map((user, index) => (
                        <Draggable
                          key={user.id}
                          draggableId={user.id.toString()}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`user-item ${snapshot.isDragging ? 'dragging' : ''}`}
                            >
                              <OverlayTrigger
                                placement="right"
                                delay={{ show: 200, hide: 100 }}
                                overlay={
                                  <Tooltip id={`sidebar-tooltip-${user.id}`} className="bs-tooltip-end">
                                    {(() => {
                                      if (user.firstName || user.lastName) {
                                        return `${user.firstName || ''} ${user.lastName || ''}`.trim();
                                      } else if (user.username) {
                                        return user.username;
                                      } else {
                                        return 'Unknown User';
                                      }
                                    })()} - Drag to assign
                                  </Tooltip>
                                }
                              >
                                <div className="user-avatar" style={!user.imageUrl ? {
                                  backgroundColor: getUserAvatarColor(user),
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'white',
                                  fontSize: '14px',
                                  fontWeight: '500'
                                } : undefined}>
                                  {user.imageUrl ? (
                                    <ProfileImage
                                      imageUrl={user.imageUrl}
                                      alt={user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.username}
                                    />
                                  ) : (
                                    <span>{getUserInitials(user)}</span>
                                  )}
                                </div>
                              </OverlayTrigger>
                              {!sidebarCollapsed && (
                                <div className="user-info">
                                    <div className="user-name">
                                        {(() => {
                                            if (user.firstName || user.lastName) {
                                                // If either first name or last name exists, use what's available
                                                return `${user.firstName || ''} ${user.lastName || ''}`.trim();
                                            } else if (user.username) {
                                                // If both names are null/empty but username exists, use username
                                                return user.username;
                                            } else {
                                                // Last resort
                                                return 'Unknown User';
                                            }
                                        })()}
                                    </div>
                                    <div className="user-email">{user.email || ''}</div>
                                </div>
                              )}
                            </div>
                          )}
                        </Draggable>
                      ))
                    ) : (
                      <div className="no-users">
                        {searchTerm ? 'No users match your search' : 'No unassigned users'}
                      </div>
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            )}
          </div>
          <div 
            className={`chart-area ${isDragging ? 'grabbing' : ''}`}
            ref={chartContentRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <div 
              className="chart-area-content"
              style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
              }}
            >
              {Array.isArray(chartData) ? (
                <Tree
                  lineWidth={'2px'}
                  lineColor={'#ccc'}
                  lineBorderRadius={'5px'}
                  lineHeight={'20px'}
                  nodePadding={'20px'}
                  label={
                    <div className="org-root-label">
                      <div className="org-root-logo">
                        {organizationLogo ? (
                          <img 
                            src={organizationLogo} 
                            alt={organizationName} 
                            style={{ width: '80px', height: '80px', objectFit: 'contain' }}
                          />
                        ) : (
                          <FaBuilding size={40} />
                        )}
                      </div>
                      <div className="org-root-name">
                        {organizationName}
                      </div>
                    </div>
                  }
                  className="org-chart-tree"
                >
                  {chartData.map(rootUnit => renderTree(rootUnit))}
                </Tree>
              ) : (
                <Tree
                  lineWidth={'2px'}
                  lineColor={'#ccc'}
                  lineBorderRadius={'5px'}
                  lineHeight={'20px'}
                  nodePadding={'20px'}
                  label={
                    <div className="org-root-label">
                      <div className="org-root-logo">
                        {organizationLogo ? (
                          <img 
                            src={organizationLogo} 
                            alt={organizationName} 
                            style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                          />
                        ) : (
                          <FaBuilding size={40} />
                        )}
                      </div>
                      <div className="org-root-name">
                        {organizationName}
                      </div>
                    </div>
                  }
                  className="org-chart-tree"
                >
                  {renderTree(chartData)}
                </Tree>
              )}
            </div>
            <div className="zoom-controls">
              <button className="zoom-btn" onClick={handleZoomOut} title="Zoom Out">
                <FaSearchMinus />
              </button>
              <div className="zoom-level">{Math.round(scale * 100)}%</div>
              <button className="zoom-btn" onClick={handleZoomIn} title="Zoom In">
                <FaSearchPlus />
              </button>
              <button className="zoom-btn" onClick={handleZoomReset} title="Reset Zoom">
                <FaBuilding />
              </button>
            </div>
          </div>
        </div>
      </DragDropContext>
      {showModal && (
        <div className="org-chart-modal-overlay">
          <div className="org-chart-modal">
            <div className="org-chart-modal-header">
              <h3 className="org-chart-modal-title">
                {modalType === 'add' ? 'Add Unit' : modalType === 'edit' ? 'Edit Unit' : 'Delete Unit'}
              </h3>
              <button className="org-chart-modal-close" onClick={closeModal}>&times;</button>
            </div>
            <div className="org-chart-modal-body">
              {modalType === 'delete' ? (
                <>
                  <p>Are you sure you want to delete "{modalData?.name}"? This action cannot be undone.</p>
                  {modalData?.children && modalData.children.length > 0 && (
                    <div className="org-chart-alert org-chart-alert-warning">
                      <strong>Warning:</strong> This unit has child units. Deleting it will also delete all child units.
                    </div>
                  )}
                  <div className="org-chart-modal-footer">
                    <button className="org-chart-btn org-chart-btn-secondary" onClick={closeModal}>Cancel</button>
                    <button 
                      className="org-chart-btn org-chart-btn-danger" 
                      onClick={() => {
                        deleteUnit(modalData.id);
                        closeModal();
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </>
              ) : (
                <form 
                  key={formKey}
                  onSubmit={(e) => {
                    e.preventDefault();
                    const formData = {
                      id: modalData?.id,
                      name: formInputs.name,
                      description: formInputs.description,
                      unitTypeId: formInputs.unitTypeId,
                      parentId: modalData?.parentId
                    };
                    handleFormSubmit(formData);
                  }}
                >
                  <div className="form-group">
                    <label className="form-label" htmlFor="name">Unit Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="form-control"
                      value={formInputs.name}
                      onChange={(e) => setFormInputs({ ...formInputs, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="description">Description</label>
                    <textarea
                      id="description"
                      name="description"
                      className="form-control"
                      value={formInputs.description}
                      onChange={(e) => setFormInputs({ ...formInputs, description: e.target.value })}
                      rows="3"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="unitTypeId">Unit Type</label>
                    {unitTypes.length > 0 ? (
                      <select
                        id="unitTypeId"
                        name="unitTypeId"
                        className="form-control"
                        value={formInputs.unitTypeId || ''}
                        onChange={(e) => setFormInputs({ ...formInputs, unitTypeId: parseInt(e.target.value) })}
                        required
                      >
                        <option value="">Select a unit type</option>
                        {unitTypes.map(type => (
                          <option key={type.id} value={type.id}>{type.name}</option>
                        ))}
                      </select>
                    ) : (
                      <div className="org-chart-alert org-chart-alert-warning">
                        No unit types available. Please create unit types first.
                      </div>
                    )}
                  </div>
                  <div className="org-chart-modal-footer">
                    <button type="button" className="org-chart-btn org-chart-btn-secondary" onClick={closeModal}>Cancel</button>
                    <button 
                      type="submit" 
                      className="org-chart-btn org-chart-btn-primary"
                      disabled={unitTypes.length === 0}
                    >
                      {modalType === 'add' ? 'Add' : 'Save'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
      {showAssignModal && (
        <div className="org-chart-modal-overlay">
          <div className="org-chart-modal">
            <div className="org-chart-modal-header">
              <h3>Assign User to Unit</h3>
              <button 
                className="org-chart-modal-close" 
                onClick={() => {
                  setShowAssignModal(false);
                  setDraggedUserData(null);
                  setTargetUnitData(null);
                }}
              >
                &times;
              </button>
            </div>
            <div className="org-chart-modal-body">
              <form onSubmit={handleAssignmentSubmit}>
                <div className="form-group">
                  <label className="form-label">
                    Assigning: <strong>{draggedUserData?.user?.firstName} {draggedUserData?.user?.lastName}</strong>
                  </label>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    To Unit: <strong>{targetUnitData?.unit?.name}</strong>
                  </label>
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="positionTitle">Position Title *</label>
                  <input
                    type="text"
                    id="positionTitle"
                    className="form-control"
                    value={assignmentFormData.positionTitle}
                    onChange={(e) => setAssignmentFormData({
                      ...assignmentFormData,
                      positionTitle: e.target.value
                    })}
                    required
                  />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    Is Manager
                    <input
                      type="checkbox"
                      checked={assignmentFormData.isManager}
                      onChange={(e) => setAssignmentFormData({
                        ...assignmentFormData,
                        isManager: e.target.checked
                      })}
                      style={{ marginLeft: '8px' }}
                    />
                  </span>
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    Include Appointment Details
                    <input
                      type="checkbox"
                      checked={includeAppointment}
                      onChange={(e) => setIncludeAppointment(e.target.checked)}
                      style={{ marginLeft: '8px' }}
                    />
                  </span>
                </div>
                
                {includeAppointment && (
                  <>
                    <div className="form-group">
                      <label className="form-label" htmlFor="roleName">Role Name</label>
                      <input
                        type="text"
                        id="roleName"
                        className="form-control"
                        value={assignmentFormData.roleName}
                        onChange={(e) => setAssignmentFormData({
                          ...assignmentFormData,
                          roleName: e.target.value
                        })}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="positionMultiplier">Position Multiplier</label>
                      <input
                        type="number"
                        id="positionMultiplier"
                        className="form-control"
                        value={assignmentFormData.positionMultiplier}
                        onChange={(e) => setAssignmentFormData({
                          ...assignmentFormData,
                          positionMultiplier: parseFloat(e.target.value)
                        })}
                        step="0.1"
                        min="0"
                      />
                      <small className="text-muted">
                        Based on unit type multiplier: {unitTypeMultiplier}
                      </small>
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="employmentType">Employment Type</label>
                      <select
                        id="employmentType"
                        className="form-control"
                        value={`${assignmentFormData.employmentType}|${assignmentFormData.employmentTypeMultiplier}`}
                        onChange={(e) => {
                          const [name, multiplier] = e.target.value.split('|');
                          setAssignmentFormData({
                            ...assignmentFormData,
                            employmentType: name,
                            employmentTypeMultiplier: parseFloat(multiplier)
                          });
                        }}
                        required
                      >
                        <option value="">Select Employment Type</option>
                        {employmentTypes.map(type => (
                          <option key={type.id} value={`${type.name}|${type.multiplier}`}>
                            {type.name} (x{type.multiplier})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="experienceLevel">Experience Level</label>
                      <select
                        id="experienceLevel"
                        className="form-control"
                        value={`${assignmentFormData.experienceLevelName}|${assignmentFormData.experienceLevelMultiplier}`}
                        onChange={(e) => {
                          const [name, multiplier] = e.target.value.split('|');
                          setAssignmentFormData({
                            ...assignmentFormData,
                            experienceLevelName: name,
                            experienceLevelMultiplier: parseFloat(multiplier)
                          });
                        }}
                        required
                      >
                        <option value="">Select Experience Level</option>
                        {experienceLevels.map(level => (
                          <option key={level.id} value={`${level.name}|${level.multiplier}`}>
                            {level.name} (x{level.multiplier})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="educationLevel">Education Level</label>
                      <select
                        id="educationLevel"
                        className="form-control"
                        value={`${assignmentFormData.educationLevelName}|${assignmentFormData.educationlevelMultiplier}`}
                        onChange={(e) => {
                          const [name, multiplier] = e.target.value.split('|');
                                                      setAssignmentFormData({
                              ...assignmentFormData,
                              educationLevelName: name,
                              educationlevelMultiplier: parseFloat(multiplier)
                            });
                        }}
                        required
                      >
                        <option value="">Select Education Level</option>
                        {educationLevels.map(level => (
                          <option key={level.id} value={`${level.name}|${level.multiplier}`}>
                            {level.name} (x{level.multiplier})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="organizationalLevel">Organizational Level</label>
                      <select
                        id="organizationalLevel"
                        className="form-control"
                        value={`${assignmentFormData.organizationalLevelName}|${assignmentFormData.organizationalLevelMultiplier}`}
                        onChange={(e) => {
                          const [name, multiplier] = e.target.value.split('|');
                          setAssignmentFormData({
                            ...assignmentFormData,
                            organizationalLevelName: name,
                            organizationalLevelMultiplier: parseFloat(multiplier)
                          });
                        }}
                        required
                      >
                        <option value="">Select Organizational Level</option>
                        {organizationalLevels.map(level => (
                          <option key={level.id} value={`${level.name}|${level.multiplier}`}>
                            {level.name} (x{level.multiplier})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="impactFactor">Impact Factor</label>
                      <input
                        type="number"
                        id="impactFactor"
                        className="form-control"
                        value={assignmentFormData.impactFactor}
                        onChange={(e) => setAssignmentFormData({
                          ...assignmentFormData,
                          impactFactor: parseFloat(e.target.value)
                        })}
                        step="0.1"
                        min="0"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="roleImportance">Role Importance</label>
                      <input
                        type="number"
                        id="roleImportance"
                        className="form-control"
                        value={assignmentFormData.roleImportance?.toFixed(2) || '0.00'}
                        disabled
                        step="0.01"
                      />
                      <small className="text-muted">
                        Exact calculation based on position, experience, education, organizational level, and impact factor
                      </small>
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="xPortion">X-Portion Value</label>
                      <input
                        type="number"
                        id="xPortion"
                        className="form-control"
                        value={assignmentFormData.xPortion}
                        disabled
                      />
                      <small className="text-muted">
                        {latestXPortion ? 'Latest X-Portion value' : 'Default value: 1.0'}
                      </small>
                    </div>
                    <div className="form-row">
                      <div className="form-group form-group-half">
                        <label className="form-label" htmlFor="startDate">Start Date</label>
                        <input
                          type="date"
                          id="startDate"
                          className="form-control"
                          value={assignmentFormData.startDate}
                          onChange={(e) => setAssignmentFormData({
                            ...assignmentFormData,
                            startDate: e.target.value
                          })}
                          required
                        />
                      </div>
                      <div className="form-group form-group-half">
                        <label className="form-label" htmlFor="endDate">End Date</label>
                        <input
                          type="date"
                          id="endDate"
                          className="form-control"
                          value={assignmentFormData.endDate || ''}
                          onChange={(e) => setAssignmentFormData({
                            ...assignmentFormData,
                            endDate: e.target.value || null
                          })}
                          min={assignmentFormData.startDate}
                        />
                      </div>
                    </div>
                  </>
                )}
                
                <div className="org-chart-modal-footer">
                  <button 
                    type="button" 
                    className="org-chart-btn org-chart-btn-secondary"
                    onClick={() => {
                      setShowAssignModal(false);
                      setDraggedUserData(null);
                      setTargetUnitData(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="org-chart-btn org-chart-btn-primary"
                  >
                    Assign User
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {showUsersModal && selectedUnitUsers && (
        <UsersModal
          users={selectedUnitUsers.users}
          unitName={selectedUnitUsers.unitName}
          unitId={selectedUnitUsers.unitId}
          onClose={() => {
            setShowUsersModal(false);
            setSelectedUnitUsers(null);
          }}
        />
      )}
      {showUnitTypesModal && (
        <UnitTypesModal
          isOpen={showUnitTypesModal}
          onClose={() => setShowUnitTypesModal(false)}
          unitTypes={unitTypes}
          onSave={handleSaveUnitType}
          onDelete={handleDeleteUnitType}
        />
      )}
      {showUserManagerModal && selectedUserForManager && selectedUnitForManager && (
        <div className="org-chart-modal-overlay">
          <div className="org-chart-modal">
            <div className="org-chart-modal-header">
              <h3>Manage User Role</h3>
              <button 
                className="org-chart-modal-close" 
                onClick={() => {
                  setShowUserManagerModal(false);
                  setSelectedUserForManager(null);
                  setSelectedUnitForManager(null);
                  setTempManagerStatus(false);
                }}
              >
                &times;
              </button>
            </div>
            <div className="org-chart-modal-body">
              <div className="user-manager-info">
                <div className="user-avatar-large">
                  {renderUserAvatar(selectedUserForManager)}
                </div>
                <div className="user-details">
                  <h4>
                    {selectedUserForManager.userFullName || 
                     `${selectedUserForManager.firstName || ''} ${selectedUserForManager.lastName || ''}`.trim() ||
                     selectedUserForManager.username || 'User'}
                  </h4>
                  <p className="unit-name">In: <strong>{selectedUnitForManager.name}</strong></p>
                  {selectedUserForManager.email && (
                    <p className="user-email">{selectedUserForManager.email}</p>
                  )}
                </div>
              </div>
              
                             <div className="manager-checkbox-container">
                 <label className="manager-checkbox-label">
                   <input
                     type="checkbox"
                     checked={tempManagerStatus}
                     onChange={(e) => setTempManagerStatus(e.target.checked)}
                     className="manager-checkbox"
                   />
                   <span className="checkmark"></span>
                   <span className="label-text">
                     Is Manager
                   </span>
                 </label>
                 <p className="manager-description">
                   {tempManagerStatus 
                     ? 'This user will have manager privileges in this unit.' 
                     : 'This user will be a regular employee in this unit.'}
                 </p>
               </div>
            </div>
            <div className="org-chart-modal-footer">
              <button 
                type="button" 
                className="org-chart-btn org-chart-btn-secondary"
                onClick={() => {
                  setShowUserManagerModal(false);
                  setSelectedUserForManager(null);
                  setSelectedUnitForManager(null);
                  setTempManagerStatus(false);
                }}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="org-chart-btn org-chart-btn-primary"
                onClick={handleSaveManagerStatus}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationalChart; 